import React, { useMemo } from 'react'
import { FlexRow } from 'src/components/Reusable/Flex'
import {
  TooltipPositions,
  TooltipOverlayType,
  Tooltip,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AUDIT_RESULT } from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import { NoPaddingGridColumn } from 'src/components/Reusable/Grid/Grid.styles'
import { TitleText } from 'src/components/Reusable/Text/Title'
import { KeyedMutator } from 'swr'
import {
  ControlPolicyInfo,
  PolicyInfo,
} from 'src/components/Reusable/ListItem/ListItem.components'
import { useGetControlChecks } from 'src/pages/GRCDashboards/Compliance/ControlChecksDashboardWidget/ControlChecksWidget.hooks'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import {
  Identifier,
  MODEL_TYPE,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import { ControlStatusRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import isFunction from 'lodash/isFunction'
import { LIST_ITEM_COPY } from 'src/components/Reusable/ListItem/ListItem.constants'
import { ControlStatusDropdown } from 'src/components/PageLayout/ShowPage/Dropdowns/ControlStatusDropdown'
import { ContentMatchBadge } from 'src/components/Reusable/Search/ContentSearch/ContentSearch.components'
import { ActionGridColumn, GridColumn } from '../../../components/Reusable/Grid'
import { DepartmentChip } from '../DepartmentChip'
import { BasicChip } from '../../../components/Reusable/Chips/BasicChip/BasicChip'
import { ModelComplianceFrameWorkInfo } from '../../../components/Reusable/Text/ModelComplianceFrameworkInfo'
import { useComplianceFrameworks } from '../../../components/async/complianceframework/useComplianceFrameworks'
import { MODEL_TYPES } from '../../../Utils/globalEnums'
import { StyledModelId } from '../ControlsShowPage/ControlsShowPage.styles'
import { OwnerAssignmentButton } from './OwnerAssignmentButton'
import { DescriptionMarkup } from './DescriptionMarkup'
import { ControlsListItemMenu } from './ControlsListItemMenu/ControlsListItemMenu'
import {
  StyledIsStaleResult,
  PolicyGridColumn,
} from './ControlsListItem.styles'
import {
  getCheckResultConfig,
  getComplianceFrameworksForControl,
  getControlsListItemConfig,
  getMismatch,
  getNoCheckResultConfig,
} from './ControlsListItem.helpers'
import {
  ControlCheckConfigType,
  ControlListItemConfig,
} from './ControlListItem.constants'

const MAX_MODEL_ID_LENGTH = 20

export const ControlModelId = ({
  modelId,
}: {
  modelId: string
}): JSX.Element => {
  return modelId.length > MAX_MODEL_ID_LENGTH ? (
    <Tooltip
      id={`controls-index-row-modelid-tooltip-${modelId}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={modelId}
    >
      <StyledModelId>{modelId}</StyledModelId>
    </Tooltip>
  ) : (
    <>{modelId}</>
  )
}

export const ControlChecksListItemNoTestResult = ({
  controlId,
  isNotApplicable,
}: {
  controlId: string
  isNotApplicable: boolean
}): JSX.Element => {
  const checkConfig = getNoCheckResultConfig(controlId, isNotApplicable)
  return (
    <ControlsChecksListItemTestResultContainer config={checkConfig}>
      {checkConfig.icon}
    </ControlsChecksListItemTestResultContainer>
  )
}

export const ControlChecksListItemTestResult = ({
  controlId,
  result,
  isStale,
}: {
  controlId: string
  result: AUDIT_RESULT
  isStale: boolean
}): JSX.Element => {
  const checkConfig = getCheckResultConfig(controlId, result, isStale)
  return (
    <ControlsChecksListItemTestResultContainer config={checkConfig}>
      {checkConfig.icon}
    </ControlsChecksListItemTestResultContainer>
  )
}

const ControlsChecksListItemTestResultContainer = ({
  config,
  children,
}: {
  config: ControlCheckConfigType
  children: JSX.Element
}) => (
  <Tooltip
    id={config.tooltipId}
    placement={TooltipPositions.left}
    overlayType={TooltipOverlayType.popover}
    tooltipBody={config.tooltipBody}
  >
    {config.isStale ? (
      <StyledIsStaleResult>{children}</StyledIsStaleResult>
    ) : (
      <FlexRow isFullWidth isFullHeight>
        {children}
      </FlexRow>
    )}
  </Tooltip>
)

export const ControlsListItemMenuColumn = ({
  control,
  config,
}: {
  control: Control.AsObject
  config?: ControlListItemConfig
}): JSX.Element => {
  const menuConfig = getControlsListItemConfig(config)
  return (
    <ActionGridColumn>
      {menuConfig?.customMenu ? (
        <menuConfig.customMenu control={control} />
      ) : (
        <ControlsListItemMenu control={control} />
      )}
    </ActionGridColumn>
  )
}

export const ControlsListItemAssigneeColumn = ({
  control,
  mutate,
}: {
  control: Control.AsObject
  mutate: KeyedMutator<Control>
}): JSX.Element => (
  <GridColumn>
    <OwnerAssignmentButton control={control} mutate={mutate} />
  </GridColumn>
)

export const ControlsListItemStatusColumn = ({
  control,
  mutate,
}: {
  control: Control.AsObject
  mutate: KeyedMutator<Control>
}): JSX.Element => {
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)

  const updateStatus = async (status: CONTROL_STATUS) => {
    const identifierMsg = new Identifier()
      .setModelid(control.modelId)
      .setModeltype(MODEL_TYPE.CONTROL)
    const controlStatusMsg = new ControlStatusRequest()
      .setId(identifierMsg)
      .setStatus(status)
    await modelClient.setControlStatus(controlStatusMsg)
    if (isFunction(mutate)) {
      await mutate()
    }
  }

  return (
    <GridColumn>
      <ControlStatusDropdown
        control={control}
        initialStatus={control.status as CONTROL_STATUS}
        onSelect={updateStatus}
      />
    </GridColumn>
  )
}

export const ControlsListItemEvidenceColumn = ({
  show,
  control,
}: {
  show: boolean
  control: Control.AsObject
}): JSX.Element => {
  return <GridColumn>{show && <>{control.linkedEvidenceCount}</>}</GridColumn>
}

export const ControlsListItemDepartmentColumn = ({
  show,
  departmentId,
  departmentNames,
}: {
  show: boolean
  departmentId: string
  departmentNames?: string[]
}): JSX.Element => (
  <GridColumn>
    {show &&
      (departmentNames && departmentNames.length > 0 ? (
        <DepartmentSummaryBadge departmentNames={departmentNames} />
      ) : (
        <DepartmentChip departmentId={departmentId} />
      ))}
  </GridColumn>
)

const DepartmentSummaryBadge = ({
  departmentNames,
}: {
  departmentNames: string[]
}): JSX.Element => {
  const uniqueDepartmentNames = useMemo(
    () => Array.from(new Set(departmentNames.filter(Boolean))),
    [departmentNames],
  )

  if (uniqueDepartmentNames.length === 0) {
    return <BasicChip>-</BasicChip>
  }

  const [primaryDepartment, ...remainingDepartments] = uniqueDepartmentNames
  const hasMore = remainingDepartments.length > 0

  return hasMore ? (
    <Tooltip
      id={`controls-index-departments-tooltip-${uniqueDepartmentNames.join(
        '-',
      )}`}
      placement={TooltipPositions.top}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={remainingDepartments.join(', ')}
    >
      <div>
        <BasicChip>{`${primaryDepartment} + ${remainingDepartments.length} more`}</BasicChip>
      </div>
    </Tooltip>
  ) : (
    <BasicChip>{primaryDepartment}</BasicChip>
  )
}

export const ControlsListItemContentMatchColumn = ({
  isContentMatch,
}: {
  isContentMatch: boolean
}): JSX.Element => (
  <GridColumn>
    <ContentMatchBadge isContentMatch={isContentMatch} />
  </GridColumn>
)

export const ControlsListItemModelIdColumn = ({
  modelId,
}: {
  modelId: string
}): JSX.Element => (
  <GridColumn>
    <ControlModelId modelId={modelId} />
  </GridColumn>
)

export const ControlsListItemControlNameColumn = ({
  control,
  isControlNa,
  policyNames,
}: {
  control: Control.AsObject
  isControlNa: boolean
  policyNames?: string[]
}): JSX.Element => (
  <PolicyGridColumn>
    <Tooltip
      id={`controls-index-row-tooltip-${control.name}`}
      placement={TooltipPositions.right}
      overlayType={TooltipOverlayType.popover}
      tooltipBody={<DescriptionMarkup objective={control.objective} />}
    >
      <TitleText>{control.name}</TitleText>
    </Tooltip>
    {policyNames && policyNames.length > 1 ? (
      <Tooltip
        id={`controls-index-row-policies-tooltip-${control.id}`}
        placement={TooltipPositions.top}
        overlayType={TooltipOverlayType.popover}
        tooltipBody={policyNames.join(', ')}
      >
        <div style={{ width: '100%', minWidth: 0 }}>
          <PolicyInfo policyName={policyNames.join(', ')} isWarning={false} />
        </div>
      </Tooltip>
    ) : (
      <ControlPolicyInfo
        control={{
          ...control,
          policyName:
            policyNames && policyNames.length > 0
              ? policyNames.join(', ')
              : control.policyName,
        }}
        isControlNa={isControlNa}
      />
    )}
  </PolicyGridColumn>
)

export const ControlsListItemFrameworksColumn = ({
  audit,
  complianceFrameworkIds,
}: {
  audit?: AuditRecord
  complianceFrameworkIds: string[]
}): JSX.Element => {
  const { data: cfData, isLoading: isCfLoading } = useComplianceFrameworks(true)

  if (!cfData || isCfLoading) {
    return (
      <GridColumn>
        <></>
      </GridColumn>
    )
  }

  const complianceFrameworks = cfData.getItemsList() || []

  const complianceFrameworksForControl = getComplianceFrameworksForControl(
    complianceFrameworks,
    complianceFrameworkIds,
  )
  const complianceFrameworksInUse = audit
    ? audit.getComplianceFrameworksList()
    : complianceFrameworks
  const mismatch = getMismatch(
    complianceFrameworksForControl.map((el) => el.getModelId()),
    complianceFrameworksInUse,
    !!audit,
  )
  const frameworkNames = complianceFrameworksForControl
    .map((framework) => framework.getName())
    .join(', ')
  const hasComplianceFrameworks = complianceFrameworksForControl.length > 0
  const cfNames = hasComplianceFrameworks
    ? frameworkNames
    : LIST_ITEM_COPY.NO_FRAMEWORK

  return (
    <GridColumn>
      <ModelComplianceFrameWorkInfo
        modelComplianceFrameworkNames={cfNames}
        isWarning={!hasComplianceFrameworks}
        mismatch={mismatch}
        modelType={MODEL_TYPES.CONTROL}
      />
    </GridColumn>
  )
}

export const ControlsListItemCheckColumns = ({
  controlId,
  isNotApplicable,
  migratedControlId,
}: {
  controlId: string
  isNotApplicable: boolean
  migratedControlId?: string
}): JSX.Element => {
  // use the control id to get the test results
  const { tests } = useGetControlChecks(controlId, true, migratedControlId)

  if (!tests || isNotApplicable) {
    return (
      <>
        <NoPaddingGridColumn>
          <ControlChecksListItemNoTestResult
            controlId={controlId}
            isNotApplicable={isNotApplicable}
          />
        </NoPaddingGridColumn>
        <NoPaddingGridColumn>
          <ControlChecksListItemNoTestResult
            controlId={controlId}
            isNotApplicable={isNotApplicable}
          />
        </NoPaddingGridColumn>
        <NoPaddingGridColumn>
          <ControlChecksListItemNoTestResult
            controlId={controlId}
            isNotApplicable={isNotApplicable}
          />
        </NoPaddingGridColumn>
      </>
    )
  }

  const controlMatchesPolicy = tests.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.POLICY_MATCH,
  )

  const completeness = tests.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.COMPLETENESS,
  )
  const spotCheck = tests.find(
    (test) => test.getTestName() === CONTROL_TEST_NAMES.SPOT_CHECK,
  )

  return (
    <>
      <NoPaddingGridColumn>
        {controlMatchesPolicy ? (
          <ControlChecksListItemTestResult
            controlId={controlId}
            isStale={!!controlMatchesPolicy.getIsStale()}
            result={controlMatchesPolicy.getResult()}
          />
        ) : (
          <ControlChecksListItemNoTestResult
            controlId={controlId}
            isNotApplicable={isNotApplicable}
          />
        )}
      </NoPaddingGridColumn>
      <NoPaddingGridColumn>
        {completeness ? (
          <ControlChecksListItemTestResult
            controlId={controlId}
            isStale={!!completeness.getIsStale()}
            result={completeness.getResult()}
          />
        ) : (
          <ControlChecksListItemNoTestResult
            controlId={controlId}
            isNotApplicable={isNotApplicable}
          />
        )}
      </NoPaddingGridColumn>
      <NoPaddingGridColumn>
        {spotCheck ? (
          <ControlChecksListItemTestResult
            controlId={controlId}
            isStale={!!spotCheck.getIsStale()}
            result={spotCheck.getResult()}
          />
        ) : (
          <ControlChecksListItemNoTestResult
            controlId={controlId}
            isNotApplicable={isNotApplicable}
          />
        )}
      </NoPaddingGridColumn>
    </>
  )
}
